import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getApiUrl, API_ENDPOINTS } from '../utils/api'

// sessionStorage key that records the last OAuth code we've attempted to
// exchange. Survives StrictMode remounts, HMR, page refresh, and back-button
// navigation — all of which could otherwise resurface a consumed code and
// trigger an "invalid_grant" error on the duplicate POST.
const CONSUMED_CODE_KEY = 'consumed_oauth_code'

const AuthCallback = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { checkAuth } = useAuth()
  const [error, setError] = useState(null)

  useEffect(() => {
    const code = searchParams.get('code')
    const oauthError = searchParams.get('error')

    if (oauthError) {
      setError('Authentication failed. Please try again.')
      setTimeout(() => navigate('/', { replace: true }), 3000)
      return
    }

    if (!code) {
      setError('No authorization code received')
      setTimeout(() => navigate('/', { replace: true }), 3000)
      return
    }

    // Store checkout Discord-identify flow (state=store): no JWT, just the
    // buyer's Discord id, then back to the sign-in page.
    if (searchParams.get('state') === 'store') {
      // Dedup (StrictMode/refresh): only the first invocation runs the exchange.
      // identifyStoreDiscord full-reloads to /signin when done so the cart
      // context re-reads the stored Discord id.
      if (sessionStorage.getItem(CONSUMED_CODE_KEY) === code) return
      sessionStorage.setItem(CONSUMED_CODE_KEY, code)
      identifyStoreDiscord(code)
      return
    }

    // If we've already tried this exact code (refresh, back nav, HMR remount,
    // StrictMode double-fire), don't POST it again — Discord rejects re-use.
    // Token from the original successful exchange is in localStorage; just
    // route to the right place.
    if (sessionStorage.getItem(CONSUMED_CODE_KEY) === code) {
      const haveToken = !!localStorage.getItem('discord_token')
      navigate(haveToken ? '/staff' : '/', { replace: true })
      return
    }

    sessionStorage.setItem(CONSUMED_CODE_KEY, code)
    exchangeCodeForToken(code)
  }, [searchParams, navigate])

  const identifyStoreDiscord = async (code) => {
    let ok = false
    try {
      const res = await fetch(getApiUrl('/api/store/discord-identify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirectUri: window.location.origin + '/auth/callback' }),
      })
      if (res.ok) {
        const data = await res.json()
        localStorage.setItem('storeDiscord', JSON.stringify({ discordId: data.discordId, username: data.username }))
        sessionStorage.setItem('storeDiscordJustLinked', '1') // one-shot: lets the store toast "connected"
        ok = true
      }
    } catch (err) {
      console.warn('Store Discord identify failed:', err)
    }
    // Return to wherever store sign-in started. The Valley storefront stashes
    // its path in `storeReturn`; MWRP's flow doesn't, so it falls back to the
    // legacy /signin landing. Full reload (not client-side nav) so the cart
    // context re-reads the stored Discord id.
    let dest = '/signin'
    try { dest = sessionStorage.getItem('storeReturn') || '/signin' } catch { /* ignore */ }
    sessionStorage.removeItem('storeReturn')
    window.location.replace(ok ? dest : `${dest}${dest.includes('?') ? '&' : '?'}discord_error=1`)
  }

  const exchangeCodeForToken = async (code) => {
    try {
      const apiUrl = getApiUrl(API_ENDPOINTS.AUTH_CALLBACK)

      // The redirect URI passed back to Discord must match what was used to
      // start the flow. window.location.origin keeps this correct on any
      // tenant domain (mwrp.gg, valleyrp.gg, localhost, dev hosts).
      const redirectUri = window.location.origin + '/auth/callback'

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, redirectUri }),
      })

      if (response.ok) {
        const data = await response.json()
        localStorage.setItem('discord_token', data.token)
        await checkAuth()
        // Return to the dashboard page they were trying to open (set by
        // ProtectedRoute / login()), else the dashboard home. replace: true so
        // the back button can't bring them back to the consumed callback URL.
        let dest = '/staff'
        try { dest = sessionStorage.getItem('staffReturn') || '/staff'; sessionStorage.removeItem('staffReturn') } catch { /* ignore */ }
        navigate(dest.startsWith('/staff') ? dest : '/staff', { replace: true })
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Authentication failed')
        // Failed exchange — wipe the dedup marker so the user can retry
        // with a fresh code after navigating back to /.
        sessionStorage.removeItem(CONSUMED_CODE_KEY)
        setTimeout(() => navigate('/', { replace: true }), 3000)
      }
    } catch (err) {
      console.error('Token exchange failed:', err)
      setError('Failed to complete authentication')
      sessionStorage.removeItem(CONSUMED_CODE_KEY)
      setTimeout(() => navigate('/', { replace: true }), 3000)
    }
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#0A0A0F] via-[#12121A] to-[#0A0A0F]">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <p className="text-[#64748B]">Redirecting to login...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#0A0A0F] via-[#12121A] to-[#0A0A0F]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3B82F6] mx-auto mb-4"></div>
        <p className="text-[#64748B]">Completing authentication...</p>
      </div>
    </div>
  )
}

export default AuthCallback

